import { lazy, Suspense, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { FractalShell } from '../components/fractal/FractalShell';
import { initialView } from '../components/fractal/model';
import { PROJECTS } from '../constants';
import { CASE_STUDIES, type CaseStudy as Study } from '../data/caseStudies';
import { useLocale } from '../i18n';
import { caseStudyPath, htmlLang, sectionPath } from '../seo/routes';
import NotFound from './NotFound';

const FractalExplorer = lazy(() => import('../components/fractal/FractalExplorer'));

const STUDY_ORDER: readonly string[] = PROJECTS.filter((p) => p.featured && CASE_STUDIES[p.slug]).map((p) => p.slug);

function nextStudy(slug: string): { study: Study; position: number } {
  const index = STUDY_ORDER.indexOf(slug);
  const nextSlug = STUDY_ORDER[(index + 1) % STUDY_ORDER.length];
  const study = CASE_STUDIES[nextSlug];
  if (!study) throw new Error(`Case study order points at a missing study: ${nextSlug}`);
  return { study, position: STUDY_ORDER.indexOf(nextSlug) + 1 };
}

const pad = (n: number) => String(n).padStart(2, '0');

const Section = ({ index, title, children }: { index: number; title: string; children: ReactNode }) => (
  <section>
    <h2 className="eyebrow mb-4 flex items-center gap-3">
      <span className="h-px w-8 bg-primary" aria-hidden="true" />
      {pad(index)} / {title}
    </h2>
    <p className="max-w-[65ch] text-lg leading-relaxed text-foreground/85">{children}</p>
  </section>
);

export const CaseStudy = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const locale = useLocale();
  const study = slug ? CASE_STUDIES[slug] : undefined;

  if (!study) return <NotFound />;

  const en = (key: string) => t(key, { lng: 'en' });
  const chromeLang = htmlLang(locale);
  const next = nextStudy(study.slug);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main lang="en" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24">
        <Link
          to={sectionPath(locale, 'projects')}
          viewTransition
          lang={chromeLang}
          className="eyebrow mb-8 inline-flex items-center gap-2 transition-colors hover:text-foreground"
        >
          <span aria-hidden="true">←</span> {t('caseStudy.back')}
        </Link>

        <p className="eyebrow mb-4 flex items-center gap-3">
          <span className="h-px w-8 bg-primary" aria-hidden="true" />
          {study.role} · {study.year}
        </p>
        <h1 className="mb-4 text-4xl md:text-6xl font-semibold tracking-tight">
          {study.title}
          <span className="text-primary">.</span>
        </h1>
        <p className="mb-8 max-w-2xl text-xl text-muted-foreground">{study.tagline}</p>

        <div className="mb-12 flex flex-wrap gap-2">
          {study.stack.map((s) => (
            <span key={s} className="badge badge-accent">
              {s}
            </span>
          ))}
        </div>

        {study.slug === 'rust-fractal' && (
          <div className="mb-14">
            <h2 className="eyebrow mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-primary" aria-hidden="true" />
              {en('caseStudy.liveDemo')}
            </h2>
            <Suspense fallback={<FractalShell view={initialView} zoom={1} status="loading" />}>
              <FractalExplorer />
            </Suspense>
          </div>
        )}

        <div className="space-y-12">
          <Section index={1} title={en('caseStudy.problem')}>{study.problem}</Section>
          <Section index={2} title={en('caseStudy.approach')}>{study.approach}</Section>
          <Section index={3} title={en('caseStudy.outcome')}>{study.outcome}</Section>
        </div>

        {study.links.length > 0 && (
          <div className="mt-12 flex flex-wrap gap-4 border-t border-border pt-8">
            {study.links.map((l) => (
              <a key={l.href} href={l.href} target="_blank" rel="noreferrer" className="btn-outline">
                {l.label} <span aria-hidden="true">↗</span>
              </a>
            ))}
          </div>
        )}

        <nav aria-label={t('caseStudy.more')} lang={chromeLang} className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="tile tile-hover">
            <Link
              to={caseStudyPath(locale, next.study.slug)}
              viewTransition
              className="group tile-in flex flex-col !p-6"
            >
              <p className="eyebrow mb-6 flex items-center justify-between gap-4">
                <span>{t('caseStudy.next')}</span>
                <span className="tabular-nums">
                  {pad(next.position)} / {pad(STUDY_ORDER.length)}
                </span>
              </p>
              <p
                lang="en"
                className="font-display text-3xl font-extrabold uppercase leading-[0.92] transition-colors group-hover:text-primary md:text-4xl"
              >
                {next.study.title}
                <span className="text-primary">.</span>
              </p>
              <p lang="en" className="mt-3 text-sm text-muted-foreground">
                {next.study.tagline}
              </p>
              <span className="mt-auto flex items-center gap-1 pt-6 text-sm text-muted-foreground transition-colors group-hover:text-primary">
                {t('projects.cta.caseStudy')} <span aria-hidden="true">→</span>
              </span>
            </Link>
          </div>
          <div className="tile tile-hover">
            <a href={sectionPath(locale, 'contact')} className="tile-in flex flex-col !p-6">
              <p className="eyebrow mb-6">{t('caseStudy.cta.eyebrow')}</p>
              <p className="font-display text-3xl font-extrabold uppercase leading-[0.92] md:text-4xl">
                {t('caseStudy.cta.title')}
                <span className="text-primary">.</span>
              </p>
              <p className="mt-3 text-sm text-muted-foreground">{t('caseStudy.cta.body')}</p>
              <span className="mt-auto flex pt-6">
                <span className="btn-primary">
                  {t('caseStudy.cta.button')} <span aria-hidden="true">→</span>
                </span>
              </span>
            </a>
          </div>
        </nav>
      </main>
      <Footer />
    </div>
  );
};

export default CaseStudy;
